/**
 * Hero terminal puzzle — animated, kind-aware (base64 · rot13 · hex), grades client-side
 * against `atob` / rot13 / hex.decode, then sets localStorage `hc_demo_unlocked` so the
 * register-then-redeem flow in `/api/me/redeem-demo` credits the +1 hint.
 *
 * Polish in this version: staggered typing animation on the prompts, blinking caret on
 * the input, shake animation on wrong submit, green-pulse on win, focus-on-mount.
 */
import { useState, useMemo, useRef, useEffect } from "react";
import { toast } from "sonner";
import { CheckCircle, Spinner } from "@phosphor-icons/react";
import { api } from "../../lib/api";

const HINT_LABEL = {
    base64: "// hint: it's base64. `echo <str> | base64 -d`",
    rot13: "// hint: rot13 — shift letters by 13.",
    hex: "// hint: hex bytes. `echo <str> | xxd -r -p`",
};

function rot13(s) {
    return s.replace(/[A-Za-z]/g, (c) => {
        const base = c <= "Z" ? 65 : 97;
        return String.fromCharCode(((c.charCodeAt(0) - base + 13) % 26) + base);
    });
}

function hexDecode(s) {
    try {
        const clean = s.replace(/\s+/g, "");
        if (clean.length % 2 !== 0) return null;
        let out = "";
        for (let i = 0; i < clean.length; i += 2) {
            out += String.fromCharCode(parseInt(clean.slice(i, i + 2), 16));
        }
        return out;
    } catch { return null; }
}

export default function DemoPuzzle({ puzzle: propPuzzle, loading }) {
    const [puzzle, setPuzzle] = useState(propPuzzle);
    useEffect(() => { setPuzzle(propPuzzle); }, [propPuzzle]);
    const [input, setInput] = useState("");
    const [error, setError] = useState("");
    const [shake, setShake] = useState(false);
    const [winPulse, setWinPulse] = useState(false);
    const [fetchingNext, setFetchingNext] = useState(false);
    const [solved, setSolved] = useState(() => {
        try { return !!localStorage.getItem("hc_demo_unlocked"); } catch { return false; }
    });
    const [streak, setStreak] = useState(() => {
        try {
            const raw = localStorage.getItem("hc_puzzle_solved_set");
            if (!raw) return 0;
            const arr = JSON.parse(raw);
            return Array.isArray(arr) ? arr.length : 0;
        } catch { return 0; }
    });
    const inputRef = useRef(null);

    // Compute the correct plaintext from the visible ciphertext + kind.
    const correct = useMemo(() => {
        if (!puzzle?.ciphertext) return null;
        const kind = puzzle.kind || "base64";
        try {
            if (kind === "base64") return atob(puzzle.ciphertext);
            if (kind === "rot13") return rot13(puzzle.ciphertext);
            if (kind === "hex") return hexDecode(puzzle.ciphertext);
        } catch { /* noop */ }
        return null;
    }, [puzzle]);

    // Auto-focus the input shortly after mount so first-time visitors see the cursor blink.
    useEffect(() => {
        if (loading || solved) return;
        const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 400);
        return () => clearTimeout(t);
    }, [loading, solved]);

    const submit = (e) => {
        e?.preventDefault?.();
        if (!correct) return;
        if (input.trim().toLowerCase() === String(correct).trim().toLowerCase()) {
            setSolved(true);
            setError("");
            setWinPulse(true);
            // Persist solve + grow streak (deduped by plaintext to prevent same-puzzle-replays)
            let nextStreak = streak;
            try {
                localStorage.setItem("hc_demo_unlocked", "1");
                localStorage.setItem("hc_demo_answer", String(correct));
                const raw = localStorage.getItem("hc_puzzle_solved_set");
                const arr = raw ? JSON.parse(raw) : [];
                const set = new Set(Array.isArray(arr) ? arr : []);
                set.add(String(correct));
                const next = Array.from(set).slice(0, 6);
                localStorage.setItem("hc_puzzle_solved_set", JSON.stringify(next));
                nextStreak = next.length;
                setStreak(nextStreak);
            } catch { /* noop */ }
            if (nextStreak >= 3) {
                toast.success(`🏆 Streak ${nextStreak}/6 — Puzzle Master unlocked · +5 hint credits on signup`);
            } else {
                toast.success(`🎉 Solved — streak ${nextStreak}/3. Solve ${3 - nextStreak} more for +5 credits & a badge.`);
            }
            setTimeout(() => setWinPulse(false), 1400);
        } else {
            setError("nope. try again.");
            setShake(true);
            setTimeout(() => setShake(false), 420);
            inputRef.current?.focus();
        }
    };

    if (loading) {
        return (
            <div className="border border-white/10 bg-[#0b121f] rounded-sm p-6 font-mono text-xs text-slate-500 flex items-center justify-center min-h-[260px]" data-testid="hero-puzzle-loading">
                <Spinner size={20} className="animate-spin text-cyan-400" />
            </div>
        );
    }

    const kind = puzzle?.kind || "base64";
    const hintLine = puzzle?.hint ? `// ${puzzle.hint}` : HINT_LABEL[kind];

    return (
        <div
            data-testid="hero-puzzle"
            className={`border bg-[#090d14] rounded-sm overflow-hidden transition-colors ${
                winPulse
                    ? "border-emerald-400/70"
                    : "border-white/10 hover:border-cyan-400/40"
            } ${shake ? "animate-shake" : ""}`}
        >
            <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 border-b border-white/[0.07] bg-[#070a10] whitespace-nowrap">
                <span className="text-[7.5px] tracking-[0.1em] min-[360px]:text-[8.5px] min-[360px]:tracking-[0.15em] sm:text-[10px] uppercase sm:tracking-[0.25em] text-slate-500 font-mono">// challenge_00 · warmup</span>
                <span className="flex items-center gap-2 text-[7.5px] min-[360px]:text-[8.5px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] font-mono shrink-0">
                    <span className="text-slate-600 border border-white/10 px-1.5 py-0.5">{kind}</span>
                    <span className="text-emerald-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />live</span>
                </span>
            </div>
            <div className="p-5 font-mono text-[13px] leading-relaxed space-y-3">
                <TypedLine delay={120}>
                    <span className="text-emerald-400">$ </span>
                    <span className="text-slate-300">cat challenge.{kind === "base64" ? "b64" : kind}</span>
                </TypedLine>
                <div className="text-slate-200 break-all bg-black/40 border border-white/[0.08] p-2.5 rounded-sm select-all" data-testid="hero-puzzle-ciphertext">
                    {puzzle?.ciphertext || "—"}
                </div>
                <TypedLine delay={480}>
                    <span className="text-emerald-400">$ </span>
                    <span className="text-slate-300">decode it. submit below. earn <span className="text-cyan-300">+1 ai hint credit</span> on signup</span>
                </TypedLine>
                {solved ? (
                    <div className="space-y-2" data-testid="hero-puzzle-solved">
                        <div className="flex items-center gap-2 text-emerald-300 bg-emerald-950/30 border border-emerald-900 p-2 rounded-sm">
                            <CheckCircle size={14} weight="fill" /> verified. sign up to claim your{streak >= 3 ? " +5 credits + puzzle master badge" : " +1 ai hint credit"}.
                        </div>
                        {streak > 0 && (
                            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-mono" data-testid="hero-puzzle-streak">
                                <span className="text-slate-500">streak</span>
                                <div className="flex gap-0.5">
                                    {[1, 2, 3].map((i) => (
                                        <span key={i} className={`w-4 h-1.5 rounded-sm ${streak >= i ? "bg-cyan-400" : "bg-slate-800"}`} />
                                    ))}
                                </div>
                                <span className={streak >= 3 ? "text-amber-300" : "text-slate-400"}>
                                    {streak}/3 {streak >= 3 ? "· puzzle master 🏆" : `· ${3 - streak} more for +5 credits`}
                                </span>
                            </div>
                        )}
                        {streak > 0 && streak < 6 && (
                            <button
                                type="button"
                                onClick={async () => {
                                    setFetchingNext(true);
                                    try {
                                        // Server picks randomly so retries usually give a new puzzle.
                                        // Retry up to 4× to avoid the same plaintext just solved.
                                        let next = null;
                                        const solvedSet = new Set();
                                        try {
                                            const raw = localStorage.getItem("hc_puzzle_solved_set");
                                            (JSON.parse(raw || "[]") || []).forEach((p) => solvedSet.add(p));
                                        } catch { /* noop */ }
                                        for (let i = 0; i < 4; i++) {
                                            const { data } = await api.get("/landing");
                                            const p = data?.puzzle;
                                            if (!p) break;
                                            // Quick local-decode to see if we've already solved this one
                                            let decoded = null;
                                            try {
                                                if (p.kind === "base64") decoded = atob(p.ciphertext);
                                                else if (p.kind === "rot13") decoded = rot13(p.ciphertext);
                                                else if (p.kind === "hex") decoded = hexDecode(p.ciphertext);
                                            } catch { /* noop */ }
                                            if (!decoded || !solvedSet.has(decoded)) { next = p; break; }
                                        }
                                        if (next) {
                                            setPuzzle(next);
                                            setSolved(false);
                                            setInput("");
                                            setError("");
                                        } else {
                                            toast.info("you've solved them all — nice. sign up to claim the badge.");
                                        }
                                    } catch { /* noop */ }
                                    setFetchingNext(false);
                                }}
                                disabled={fetchingNext}
                                data-testid="hero-puzzle-next"
                                className="text-[10px] uppercase tracking-widest font-mono text-cyan-400 hover:text-cyan-300 mt-1 disabled:opacity-50 inline-flex items-center gap-1"
                            >
                                {fetchingNext ? <Spinner size={10} className="animate-spin" /> : "→"} try another challenge · grow streak
                            </button>
                        )}
                    </div>
                ) : (
                    <form onSubmit={submit} className="flex items-center gap-2">
                        <span className="text-cyan-400">&gt;</span>
                        <div className="flex-1 relative">
                            <input
                                ref={inputRef}
                                value={input}
                                onChange={(e) => { setInput(e.target.value); setError(""); }}
                                placeholder="enter plaintext…"
                                data-testid="hero-puzzle-input"
                                className="w-full bg-transparent text-cyan-300 placeholder:text-slate-700 focus:outline-none caret-cyan-300"
                                autoComplete="off" spellCheck="false"
                            />
                        </div>
                        <button type="submit" data-testid="hero-puzzle-submit"
                                className="text-[10px] uppercase tracking-[0.2em] font-mono font-bold bg-white text-black hover:bg-cyan-300 px-3.5 py-1.5 rounded-none transition-colors">
                            run
                        </button>
                    </form>
                )}
                {error && <div className="text-rose-400 text-xs animate-fade-in" data-testid="hero-puzzle-error">{error}</div>}
                <div className="text-slate-600 text-[9px] min-[400px]:text-[10px] sm:text-[11px] pt-1 border-t border-white/10 whitespace-nowrap overflow-hidden text-ellipsis" data-testid="hero-puzzle-hint">{hintLine}</div>
            </div>

            {/* Animations — kept inline so the component is self-contained */}
            <style>{`
                @keyframes hc-shake {
                    0%, 100% { transform: translateX(0); }
                    20% { transform: translateX(-6px); }
                    40% { transform: translateX(6px); }
                    60% { transform: translateX(-4px); }
                    80% { transform: translateX(4px); }
                }
                @keyframes hc-pulse-once {
                    0% { box-shadow: 0 0 0 0 rgba(52,211,153,0.5); }
                    50% { box-shadow: 0 0 40px 8px rgba(52,211,153,0.3); }
                    100% { box-shadow: 0 0 0 0 rgba(52,211,153,0); }
                }
                @keyframes hc-caret-blink {
                    0%, 49% { opacity: 1; }
                    50%, 100% { opacity: 0; }
                }
                @keyframes hc-fade-in {
                    from { opacity: 0; transform: translateY(2px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes hc-typed-in {
                    from { opacity: 0; transform: translateX(-4px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .animate-shake { animation: hc-shake 420ms ease-in-out; }
                .animate-pulse-once { animation: hc-pulse-once 1400ms ease-out; }
                .animate-caret-blink { animation: hc-caret-blink 1.2s steps(2, start) infinite; }
                .animate-fade-in { animation: hc-fade-in 180ms ease-out; }
                .animate-typed-in { animation: hc-typed-in 260ms ease-out both; }
            `}</style>
        </div>
    );
}

function TypedLine({ children, delay = 0 }) {
    // Simple staggered fade-in to mimic a typed prompt without per-char animation cost.
    return <div className="animate-typed-in" style={{ animationDelay: `${delay}ms` }}>{children}</div>;
}
